import React, { useEffect, useRef, useState } from 'react';
import styles from './styles.styl';
import cn from 'classnames/bind';
import { Text } from '@consta/uikit/Text';
import { Button } from '@consta/uikit/Button';
import { IconUnsort } from '@consta/uikit/IconUnsort';
import AnalyticsUserAnswersCart from 'src/components/AnalyticsUserAnswersCart';
import { Pagination } from '@consta/uikit/Pagination';
import { useSelector } from 'react-redux';
import { analyticSelector } from 'src/redux/features/analytic/selectors';
import { IAnalyticResultTableEntity } from 'src/redux/features/analytic/types';
import { State } from 'src/redux/reducer';
import { SortValueEnum, SurveyTypeEnum } from 'src/utils/enum';
import { IconFunnel } from '@consta/uikit/IconFunnel';
import { ContextMenu } from '@consta/uikit/ContextMenu';
import { IconSortDown } from '@consta/uikit/IconSortDown';
import { IconSortUp } from '@consta/uikit/IconSortUp';
import { hotKeys } from 'src/config/config';

export interface IAllAnalyticsResultProps {}

const cx = cn.bind(styles);
const ITEMS_PER_PAGE = 10;

interface IFilterItems {
  name: string;
  action: () => void;
}

const AllAnalyticsResult: React.FC<IAllAnalyticsResultProps> = () => {
  const ref = useRef();
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [currentFilter, setCurrentFilter] = useState<boolean | null>(null); // пройден - не пройден
  const [currentPage, setCurrentPage] = useState<number>(0);

  const handleChange = (pageNumber: number): void => {
    setCurrentPage(pageNumber);
  };

  const results = useSelector(analyticSelector).resultTable;
  const [items, setItems] =
    useState<Array<IAnalyticResultTableEntity>>(results);

  const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);

  const typeEntity = useSelector((state: State) => state.analytic.data.type);

  const [pointsSort, setPointsSort] = useState<SortValueEnum>(
    SortValueEnum.NORMAL,
  );
  const [dateSort, setDateSort] = useState<SortValueEnum>(SortValueEnum.NORMAL);
  const [numberSort, setNumberSort] = useState<SortValueEnum>(
    SortValueEnum.INCREASE,
  );

  const sortPoints = () => {
    if (pointsSort === SortValueEnum.NORMAL) {
      setItems([...items].sort((a, b) => a?.points - b?.points));
      setPointsSort(SortValueEnum.INCREASE);
    }
    if (pointsSort === SortValueEnum.INCREASE) {
      setItems([...items].sort((a, b) => b?.points - a?.points));
      setPointsSort(SortValueEnum.DECREASE);
    }
    if (pointsSort === SortValueEnum.DECREASE) {
      setItems(
        results.filter(
          (item) =>
            item?.points >= item?.passingScore === currentFilter ||
            currentFilter === null,
        ),
      );
      setPointsSort(SortValueEnum.NORMAL);
    }
  };

  const sortDate = () => {
    if (dateSort === SortValueEnum.NORMAL) {
      setItems(
        [...items].sort((a, b) => +new Date(a?.date) - +new Date(b?.date)),
      );
      setDateSort(SortValueEnum.INCREASE);
    }
    if (dateSort === SortValueEnum.INCREASE) {
      setItems(
        [...items].sort((a, b) => +new Date(b?.date) - +new Date(a?.date)),
      );
      setDateSort(SortValueEnum.DECREASE);
    }
    if (dateSort === SortValueEnum.DECREASE) {
      setItems(
        results.filter(
          (item) =>
            item?.points >= item?.passingScore === currentFilter ||
            currentFilter === null,
        ),
      );
      setDateSort(SortValueEnum.NORMAL);
    }
  };

  const sortNumber = () => {
    setItems([...items].reverse());
    if (numberSort === SortValueEnum.INCREASE)
      setNumberSort(SortValueEnum.DECREASE);
    if (numberSort === SortValueEnum.DECREASE)
      setNumberSort(SortValueEnum.INCREASE);
  };

  useEffect(() => {
    if (currentFilter === null) return setItems(results);
    return setItems(
      results.filter(
        (item) => item?.points >= item?.passingScore === currentFilter,
      ),
    );
  }, [results, currentFilter]);

  const filterItems: IFilterItems[] = [
    {
      name: 'Пройден',
      action: () => setCurrentFilter(true),
    },
    {
      name: 'Не пройден',
      action: () => setCurrentFilter(false),
    },
  ];

  return (
    <div>
      <div className={cx('AllAnalyticsResult__sortContainer')}>
        <Text size="s">Сортировать по:</Text>
        <div className={cx('AllAnalyticsResult__sort')}>
          <div className={cx('AllAnalyticsResult__sortItem')}>
            <Text weight="bold" size="xs">
              Номер
            </Text>
            <Button
              onlyIcon
              iconLeft={
                numberSort === SortValueEnum.DECREASE
                  ? IconSortDown
                  : IconSortUp
              }
              view="clear"
              size="s"
              onClick={sortNumber}
            />
          </div>
          {typeEntity === SurveyTypeEnum.TEST && (
            <div className={cx('AllAnalyticsResult__sortItem')}>
              <Text weight="bold" size="xs">
                Баллы
              </Text>
              <Button
                onlyIcon
                iconLeft={
                  pointsSort === SortValueEnum.NORMAL
                    ? IconUnsort
                    : pointsSort === SortValueEnum.DECREASE
                    ? IconSortDown
                    : IconSortUp
                }
                view="clear"
                size="s"
                onClick={sortPoints}
              />
            </div>
          )}
          <div className={cx('AllAnalyticsResult__sortItem')}>
            <Text weight="bold" size="xs">
              Дата
            </Text>
            <Button
              onlyIcon
              iconLeft={
                dateSort === SortValueEnum.NORMAL
                  ? IconUnsort
                  : dateSort === SortValueEnum.DECREASE
                  ? IconSortDown
                  : IconSortUp
              }
              view="clear"
              size="s"
              onClick={sortDate}
            />
          </div>
          {typeEntity === SurveyTypeEnum.TEST && (
            <div className={cx('AllAnalyticsResult__sortItem')}>
              <Text weight="bold" size="xs">
                Статус
              </Text>
              <Button
                onlyIcon
                iconLeft={IconFunnel}
                view="clear"
                ref={ref}
                onClick={() => setIsOpenFilter(true)}
                size="s"
              />
              {isOpenFilter && (
                <ContextMenu
                  anchorRef={ref}
                  items={filterItems}
                  direction="downStartLeft"
                  getLabel={(item) => item.name}
                  onClickOutside={() => setIsOpenFilter(false)}
                  getOnClick={(item) => item.action}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {currentFilter !== null && (
        <div className={cx('AllAnalyticsResult__sortReset')}>
          <Text>
            Найдено {items?.length} из {results?.length} значений
          </Text>
          <Text
            onClick={() => setCurrentFilter(null)}
            view="link"
            cursor="pointer"
          >
            Сбросить фильтр
          </Text>
        </div>
      )}
      {items
        .slice(
          currentPage * ITEMS_PER_PAGE,
          currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE,
        )
        .map((item, index) => (
          <AnalyticsUserAnswersCart key={index} data={item} />
        ))}
      <div className={cx('AllAnalyticsResult__pagination')}>
        <Pagination
          currentPage={currentPage}
          onChange={handleChange}
          totalPages={totalPages}
          hotkeys={hotKeys}
        />
      </div>
    </div>
  );
};

export default AllAnalyticsResult;
